import React, { useState, useEffect, useRef } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";

interface Language {
    locale: string;
    localeLabel: string;
    localeUrl: string;
}
interface LanguageSelectorProps {
    currentLocale: string;
    preferredLocale: string;
    languages: Language[];
}

const selectorContainerStyles = css({
    fontSize: "100%",
    position: "relative",
    padding: "10px",
    [media.below.sm]: {
        fontSize: "14px",
        display: "block",
        padding: "10px 10px 10px 0"
    }
});
const openSelectorContainerSytles = css({
    [media.above.xs]: {
        backgroundColor: "#DFE1E5"
    }
});

const selectorTriggerStyles = css({
    "background": "none",
    "border": "none",
    "color": "#0052CC",
    "cursor": "pointer",
    "fontSize": "inherit",
    "display": "inline-flex",
    "alignItems": "center",
    "padding": "0px",
    "&:hover": {
        textDecoration: "underline"
    },
    "&:after": {
        content: '""',
        background: `url('https://dam-cdn.atl.orangelogic.com/AssetLink/0682k3163u1b37wc44r7aeb63180u2sp.svg') center center no-repeat`,
        display: "inline-block",
        height: "1em",
        marginInlineStart: "5px",
        transform: "rotate(180deg)",
        verticalAlign: "middle",
        width: "1em",
        transition: "transform 0.2s ease"
    }
});
const openSelectorTriggerStyles = css({
    "&:after": {
        transform: "rotate(0deg)"
    }
});

const optionsContainerStyles = css({
    marginInlineStart: "-10px",
    display: "block",
    position: "absolute",

    backgroundColor: "#F4F5F7",

    boxShadow: "6px 0px 30px 0px rgba(0,0,0,0.1)",
    marginBlockStart: "10px",
    width: "auto",
    zIndex: 99,
    whiteSpace: "nowrap",
    insetBlockEnd: "100%",
    [media.below.sm]: {
        marginInlineStart: "-15px",
        width: "100vw",
        insetBlockEnd: "200%"
    }
});
const closedOptionsContainerStyles = css({
    display: "none"
});

const optionsHeaderStyles = css({
    padding: "0 20px"
});

const spanOptionsHeaderStyles = css({
    borderBlockEnd: "1px solid #DFE1E6",

    color: "#6B778C",
    display: "block",
    padding: "10px 20px"
});

const optionsListStyles = css({
    padding: "0",
    margin: "0",
    listStyle: "none"
});

const optionItemStyles = css({
    margin: "0",
    listStyleType: "none",
    cursor: "pointer",
    textAlign: "left",
    [media.below.sm]: {
        textAlign: "left"
    }
});

const optionLinkStyles = css({
    "color": "#172B4D",
    "display": "block",
    "padding": "5px 40px",
    "textDecoration": "none",
    "backgroundColor": "transparent",
    "backgroundPosition": "initial",
    "backgroundRepeat": "no-repeat",
    "&:hover": {
        backgroundColor: "rgba(222, 235, 255, .85)",
        textDecoration: "underline"
    }
});
const selectedOptionLinkStyles = css({
    "color": "#0052CC",
    "backgroundColor": "rgba(222, 235, 255, .85)",
    "backgroundImage": `url(https://dam-cdn.atl.orangelogic.com/AssetLink/65kmcy6n8t4haf0c2cfx2b8psos2foxs.svg)`,
    "backgroundPosition": "20px center",
    "&:hover": {
        backgroundColor: "rgba(222, 235, 255, .85)"
    }
});

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
    currentLocale,
    preferredLocale,
    languages
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectorRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // Handle clicking outside of the selector
        const handleClickOutside = (event: MouseEvent) => {
            if (
                selectorRef.current &&
                !selectorRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);
    const handleTriggerClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsOpen(!isOpen);
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "languageSelectorBannerMenu",
            eventType: "ui",
            attributes: {
                page_locale: currentLocale,
                preferred_locale: preferredLocale,
                trackEventName:
                    "wac.language-selector-banner.selector-menu.clicked",
                href: window.location.href,
                page_path: window.location.pathname
            }
        }).fire();
    };

    const handleLanguageSelect = (localeUrl: string) => {
        window.location.href = localeUrl;
        setIsOpen(false);
    };

    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    return (
        <div
            css={[
                selectorContainerStyles,
                isOpen && openSelectorContainerSytles
            ]}
            ref={selectorRef}
        >
            <button
                onClick={handleTriggerClick}
                css={[
                    selectorTriggerStyles,
                    isOpen && openSelectorTriggerStyles
                ]}
                data-testid="language-selector-trigger"
            >
                <FormattedMessage
                    defaultMessage="All languages"
                    id="language-selector-banner.trigger"
                />
            </button>
            <div
                css={[
                    isOpen && optionsContainerStyles,
                    !isOpen && closedOptionsContainerStyles
                ]}
            >
                <div css={optionsHeaderStyles}>
                    <span css={spanOptionsHeaderStyles}>
                        {intl.formatMessage({
                            defaultMessage: "Choose your language",
                            id: "language-selector-banner.menu.header"
                        })}
                    </span>
                </div>
                <ul css={optionsListStyles}>
                    {languages.map((language) => (
                        <li css={optionItemStyles} key={language.locale}>
                            <a
                                href={language.localeUrl}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleLanguageSelect(language.localeUrl);
                                    createAnalyticsEvent({
                                        action: "clicked",
                                        actionSubject:
                                            "languageSelectorBannerMenuOption",
                                        eventType: "ui",
                                        attributes: {
                                            page_locale: currentLocale,
                                            preferred_locale: preferredLocale,
                                            clicked_language: language.locale,
                                            trackEventName:
                                                "wac.language-selector-banner.selector-option.clicked",
                                            href: window.location.href,
                                            page_path: window.location.pathname
                                        }
                                    }).fire();
                                }}
                                data-lang={language.locale}
                                data-locale-url={language.localeUrl}
                                css={[
                                    optionLinkStyles,
                                    currentLocale === language.locale &&
                                        selectedOptionLinkStyles
                                ]}
                            >
                                {language.localeLabel}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};
export default LanguageSelector;
